import React, { useMemo, useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
} from '@mui/material'

import useConfiguration from 'hooks/useConfiguration'
import { MODULE_NAMES } from 'utils/constants'
import usePrograms from 'hooks/programs/usePrograms'
import Autocomplete from 'components/common/Autocomplete'
import ProgramsAutocomplete from 'components/programs/ProgramsAutocomplete'

const RuleDetailsFilter = ({ rules, value, onChange, ...props }) => {
  const { getFieldDefinitions, getMetadataDisplay } = useConfiguration()
  const { programs } = usePrograms()
  const [searchValue, setSearchValue] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedField, setSelectedField] = useState(null)
  const [fieldValue, setFieldValue] = useState('')
  const [selectedProgram, setSelectedProgram] = useState(null)
  const autocompleteRef = useRef(null)
  const searchButtonRef = useRef(null)

  // Calculate isProgramIdField before useEffect
  const isProgramIdField = selectedField?.path === 'program_id'

  // Focus on search button when program is selected
  useEffect(() => {
    if (selectedProgram && isProgramIdField && searchButtonRef.current) {
      searchButtonRef.current.focus()
    }
  }, [selectedProgram, isProgramIdField])

  const options = useMemo(() => {
    const fieldDefinitions = Object.values(
      getFieldDefinitions(MODULE_NAMES.ADDRESS_OVERRIDE)
    ).map((field) => ({
      display: getMetadataDisplay(
        field.path,
        false,
        MODULE_NAMES.ADDRESS_OVERRIDE
      ),
      path: field.path,
    }))

    const _options = [
      {
        display: 'Program ID',
        path: 'program_id',
      },
    ].concat(fieldDefinitions)

    if (!searchValue.trim()) {
      return []
    }

    // Parse the search value to extract field and value
    const parseSearchValue = (input) => {
      const trimmed = input.trim()

      // Pattern 1: "field_name = value" (space equals space separated)
      const spaceEqualsMatch = trimmed.match(/^(.+?)\s*=\s*(.+)$/)
      if (spaceEqualsMatch) {
        const result = {
          field: spaceEqualsMatch[1].trim(),
          value: spaceEqualsMatch[2].trim(),
        }
        return result
      }

      // Pattern 2: "field_name value" (space separated) - but be more careful about field names
      // Look for common field patterns first - check longer patterns first
      const commonFieldPatterns = [
        /^(\w+\s+\w+\s+\w+)\s+(.+)$/, // three word field
        /^(\w+\s+\w+)\s+(.+)$/, // two word field like "member id"
        /^(\w+)\s+(.+)$/, // single word field
      ]

      for (const pattern of commonFieldPatterns) {
        const match = trimmed.match(pattern)
        if (match) {
          const result = { field: match[1].trim(), value: match[2].trim() }
          return result
        }
      }

      // Pattern 3: Check for normalized field patterns (no spaces, with underscores, etc.)
      // This handles cases like "member_id 12345", "memberid 12345"
      const normalizedPatterns = [
        /^([a-zA-Z_]+)\s+(.+)$/, // field with underscores
        /^([a-zA-Z]+)\s+(.+)$/, // field without spaces or underscores
      ]

      for (const pattern of normalizedPatterns) {
        const match = trimmed.match(pattern)
        if (match) {
          const result = { field: match[1].trim(), value: match[2].trim() }
          return result
        }
      }

      // Pattern 4: "field_name=value" (equals separated)
      const equalsMatch = trimmed.match(/^(.+?)=(.+)$/)
      if (equalsMatch) {
        const result = {
          field: equalsMatch[1].trim(),
          value: equalsMatch[2].trim(),
        }
        return result
      }

      // Pattern 5: "field_name:value" (colon separated)
      const colonMatch = trimmed.match(/^(.+?):(.+)$/)
      if (colonMatch) {
        const result = {
          field: colonMatch[1].trim(),
          value: colonMatch[2].trim(),
        }
        return result
      }

      // Pattern 6: Just a field name without value (like "member_id")
      const fieldOnlyMatch = trimmed.match(/^([a-zA-Z_]+)$/)
      if (fieldOnlyMatch) {
        const result = { field: fieldOnlyMatch[1].trim(), value: '' }
        return result
      }

      // If no pattern matches, treat the whole input as a value
      const result = { field: null, value: trimmed }
      return result
    }

    const parsed = parseSearchValue(searchValue)

    if (parsed.field) {
      // Normalize the search field for comparison
      const normalizeField = (field) => {
        return field.toLowerCase().replace(/[_\s-]/g, '') // Remove underscores, spaces, hyphens
      }

      const normalizedSearchField = normalizeField(parsed.field)

      // Try to find exact field match
      const exactMatch = _options.find((field) => {
        const normalizedPath = normalizeField(field.path)
        const normalizedDisplay = normalizeField(field.display)
        return (
          normalizedPath === normalizedSearchField ||
          normalizedDisplay === normalizedSearchField
        )
      })

      // If we have a field match, show the field option for selection
      if (exactMatch) {
        return [
          {
            display: `Search ${exactMatch.display}`,
            value: { key: exactMatch.path, value: '' },
            field: exactMatch,
          },
        ]
      }

      // Find partial field matches
      const partialMatches = _options.filter((field) => {
        const normalizedPath = normalizeField(field.path)
        const normalizedDisplay = normalizeField(field.display)
        return (
          normalizedPath.includes(normalizedSearchField) ||
          normalizedDisplay.includes(normalizedSearchField)
        )
      })

      // Show field selection options for partial matches
      if (partialMatches.length > 0) {
        return partialMatches.map((field) => ({
          display: `Search ${field.display}`,
          value: { key: field.path, value: '' },
          field: field,
        }))
      }
    } else {
      // No field specified, show all fields for selection
      return _options.map((field) => ({
        display: `Search ${field.display}`,
        value: { key: field.path, value: '' },
        field: field,
      }))
    }

    return []
  }, [getFieldDefinitions, searchValue, programs])

  const handleOptionSelect = (_e, val) => {
    if (!val) {
      onChange({
        target: {
          value: null,
        },
      })
      return
    }

    const option = options.find((option) => option.value.key === val.value.key)

    // Always open dialog for field value input
    setSelectedField(option.field)
    setFieldValue('')
    setSelectedProgram(null)
    setDialogOpen(true)
  }

  const handleDialogSubmit = () => {
    if (selectedField) {
      let newValue
      let displayValue

      if (selectedField.path === 'program_id') {
        if (selectedProgram) {
          newValue = { key: selectedField.path, value: selectedProgram.id }
          displayValue = selectedProgram.name
        } else {
          return // Don't submit if no program selected
        }
      } else {
        if (fieldValue.trim()) {
          newValue = { key: selectedField.path, value: fieldValue.trim() }
          displayValue = fieldValue.trim()
        } else {
          return // Don't submit if no value entered
        }
      }

      onChange({
        target: {
          value: {
            ruleDetails: {
              display: `${selectedField.display} = ${displayValue}`,
              value: newValue,
            },
          },
        },
      })
      setDialogOpen(false)
      setSelectedField(null)
      setFieldValue('')
      setSelectedProgram(null)
      setSearchValue(`${selectedField.display} = ${displayValue}`)
      // Blur the autocomplete
      if (autocompleteRef.current) {
        autocompleteRef.current.blur()
      }
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedField(null)
    setFieldValue('')
    setSelectedProgram(null)
    onChange({
      target: {
        value: null,
      },
    })
    // Blur the autocomplete
    if (autocompleteRef.current) {
      autocompleteRef.current.blur()
    }
  }

  return (
    <>
      <Autocomplete
        ref={autocompleteRef}
        label="Rule Details"
        name="ruleDetails"
        placeholder="Rule Details"
        options={options}
        getOptionLabel={(option) => {
          return option.display
        }}
        getOptionKey={(option) => option.display}
        isOptionEqualToValue={(option, value) => {
          return option?.value?.key === value?.value?.key
        }}
        handleAutocompleteChange={handleOptionSelect}
        value={value.ruleDetails}
        maxHeight="20vh"
        size="small"
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        {...props}
      />
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Enter Value for {selectedField?.display}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the value for {selectedField?.display}.
          </DialogContentText>
          {isProgramIdField ? (
            <ProgramsAutocomplete
              selectedPrograms={selectedProgram}
              setSelectedPrograms={setSelectedProgram}
              placeholder="Select a program"
              size="small"
              fullWidth
              multiple={false}
              textFieldProps={{ autoFocus: true }}
            />
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label="Value"
              type="text"
              fullWidth
              variant="standard"
              value={fieldValue}
              onChange={(event) => setFieldValue(event.target.value)}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleDialogSubmit()
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            ref={searchButtonRef}
            onClick={handleDialogSubmit}
            disabled={isProgramIdField ? !selectedProgram : !fieldValue.trim()}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RuleDetailsFilter
